import { Injectable, signal } from '@angular/core';
import type { ResponseAuthenticationDto } from '@kerberos-compliance/compliance-shared-ts/core/iam/authentication/dtos/response.authentication.dto';
import type { Observable } from 'rxjs';

/**
 * Manages the mutable state associated with token refresh operations.
 *
 * This store replaces the former module-level mutable variables in the
 * authentication interceptor, ensuring that refresh state is held within
 * Angular's dependency injection system as a proper singleton.
 *
 * Responsibilities:
 * - Tracking whether a refresh operation is currently in progress.
 * - Holding the shared refresh observable so concurrent 401 handlers can
 *   subscribe to the same in-flight request.
 * - Recording the timestamp of the last successful refresh to enforce a
 *   minimum interval between attempts.
 * - Maintaining a set of active request URLs to prevent duplicate retries
 *   during a refresh cycle.
 * - Providing a reset method for cleaning up state on logout or completion.
 */
@Injectable({
  providedIn: 'root',
})
export class TokenRefreshStateStore {
  public readonly minRefreshInterval = 3000;

  private readonly inProgressSignal = signal<boolean>(false);

  public readonly inProgress = this.inProgressSignal.asReadonly();

  private readonly refreshObservableSignal = signal<Observable<ResponseAuthenticationDto | undefined> | undefined>(
    undefined,
  );

  public readonly refreshObservable = this.refreshObservableSignal.asReadonly();

  private readonly lastRefreshTimestampSignal = signal<number>(0);

  public readonly lastRefreshTimestamp = this.lastRefreshTimestampSignal.asReadonly();

  private readonly authenticationFailedSignal = signal<boolean>(false);

  public readonly authenticationFailed = this.authenticationFailedSignal.asReadonly();

  private readonly activeRequests = new Set<string>();

  /**
   * Checks whether the given URL already has an active request queued during the current refresh cycle.
   *
   * @param {string} url - The request URL to check.
   * @returns {boolean} `true` if the URL is already tracked as an active request.
   */
  public hasActiveRequest(url: string): boolean {
    return this.activeRequests.has(url);
  }

  /**
   * Registers the given URL as an active request during the current refresh cycle.
   *
   * @param {string} url - The request URL to add.
   */
  public addActiveRequest(url: string): void {
    this.activeRequests.add(url);
  }

  /**
   * Removes the given URL from the set of active requests after it has been retried.
   *
   * @param {string} url - The request URL to remove.
   */
  public removeActiveRequest(url: string): void {
    this.activeRequests.delete(url);
  }

  /**
   * Removes all tracked active requests, typically called when a refresh cycle completes.
   */
  public clearActiveRequests(): void {
    this.activeRequests.clear();
  }

  /**
   * Sets whether a token refresh operation is currently in progress.
   *
   * @param {boolean} value - `true` to indicate a refresh is running, `false` to clear.
   */
  public setInProgress(value: boolean): void {
    this.inProgressSignal.set(value);
  }

  /**
   * Stores the shared observable for the current token refresh operation.
   * Pass `undefined` to clear a completed or stale observable.
   *
   * @param {Observable<ResponseAuthenticationDto | undefined> | undefined} value - The refresh observable to store, or `undefined` to clear.
   */
  public setRefreshObservable(value: Observable<ResponseAuthenticationDto | undefined> | undefined): void {
    this.refreshObservableSignal.set(value);
  }

  /**
   * Records the Unix-epoch millisecond timestamp of the last successful token refresh.
   *
   * @param {number} value - The timestamp to store.
   */
  public setLastRefreshTimestamp(value: number): void {
    this.lastRefreshTimestampSignal.set(value);
  }

  /**
   * Marks authentication as failed or resets the failure flag.
   * When set to `true`, the interceptor will block all subsequent requests.
   *
   * @param {boolean} value - `true` to indicate an irrecoverable authentication failure, `false` to clear.
   */
  public setAuthenticationFailed(value: boolean): void {
    this.authenticationFailedSignal.set(value);
  }

  /**
   * Resets all transient refresh state to its initial values.
   * Typically called during logout or after an irrecoverable authentication failure.
   */
  public resetRefreshState(): void {
    this.inProgressSignal.set(false);
    this.refreshObservableSignal.set(undefined);
    this.lastRefreshTimestampSignal.set(0);
    this.activeRequests.clear();
  }
}
